import React, { ChangeEvent, useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Autocomplete } from '@mui/material';
import { textFieldStyles } from '../../../components/Forms/textfield';
import CustomButton from '../../../components/Button/DialogButtons';
import SnackbarMessage from '../../../components/Snackbar';
import { patchRequest, postRequest } from '../baseservice';
import { AllergyreactionMaster } from '../allergyReaction';

interface IcdMasterDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: AllergyreactionMaster) => void;
    providerid: string;
    selectedAllergy: AllergyreactionMaster | null;
}

const IcdMasterDialog: React.FC<IcdMasterDialogProps> = ({
    open,
    onClose,
    onSave,
    providerid,
    selectedAllergy
}) => {
    // Form data state
    const [formData, setFormData] = useState<AllergyreactionMaster>({
        id: 0,
        provider_id: 0,
        master_type_slug: 'icd',
        name: '',
        slug: '',
        attributes: {
            
        },
        is_active: 1,
    });

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('error');

    
    // Reset form data
    const resetFormData = () => {
        setFormData({
            id: 0,
            provider_id: 0,
            master_type_slug: 'icd',
            name: '',
            slug: '',
            attributes: {
               
            },
            is_active: 1,
        });
    };

    // Handle text field changes
    const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    

    // Save form data
    const handleSave = async () => {
        try {
            const url = `resource/custom_masters/${formData.id !== 0 ? formData.id : ''}`;
            const requestData = {
                name: formData.name,
                slug: formData.slug,
                master_type_slug: formData.master_type_slug,
                is_active: formData.is_active,
                provider_id: providerid,
                attributes: formData.attributes,
            };

            // Perform PATCH if we're editing, otherwise POST
            const response = formData.id !== 0
                ? await patchRequest('resource/custom_masters/' + formData.id, requestData)
                : await postRequest('resource/custom_masters', requestData);

            if (response) {
                onSave(formData); // Callback after saving
                setSnackbarMessage(formData.id !== 0 ? 'Updated successfully!' : 'Saved successfully!');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
                resetFormData();
                onClose(); // Close dialog after save
            }
        } catch (error: any) {
            // Error handling
            if (error.response) {
                const errorMessage = error.response?.data?.data?.errors?.name || 'Unknown error occurred';
                setSnackbarMessage(errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1));
            } else if (error.request) {
                setSnackbarMessage('No response received from the server.');
            } else {
                setSnackbarMessage('An unexpected error occurred.');
            }
            setSnackbarSeverity('error');
            setSnackbarOpen(true); // Open error snackbar
        }
    };

    // Handle closing of dialog
    const handleDialogClose = () => {
        resetFormData(); // Reset the form when closing
        onClose();
    };

    // Handle Snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Populate form data if selected allergy exists (edit mode)
    useEffect(() => {
        if (selectedAllergy) {
            setFormData(selectedAllergy);
        } else {
            resetFormData();
        }
    }, [selectedAllergy]);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', fontFamily: 'Jost, sans-serif' }} className="text-center text-white font-bold bg-primary">
                    {selectedAllergy ? 'Edit ICD Masters' : 'Add  ICD Masters'}
                </DialogTitle>
                <DialogContent className="mt-4 p-4">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ fontFamily: 'Jost, sans-serif', color: 'gray' }}>Name</h2>
                                <TextField
                                    placeholder="Enter Allergy Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleTextFieldChange}
                                    fullWidth
                                    sx={textFieldStyles}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ fontFamily: 'Jost, sans-serif', color: 'gray' }}>Code</h2>
                                <TextField
                                    placeholder="Enter Code"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleTextFieldChange}
                                    fullWidth
                                    sx={textFieldStyles}
                                />
                            </div>
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions>
                    <CustomButton onClick={handleDialogClose} color="secondary">Cancel</CustomButton>
                    <CustomButton onClick={handleSave} color="primary"> {selectedAllergy ? 'Update' : 'Save'}</CustomButton>
                </DialogActions>
            </Dialog>

            
            <SnackbarMessage
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </>
    );
};

export default IcdMasterDialog;
